/**
 * Execution Recipe Store
 *
 * Caches execution_recipe_templates per workspace (built-ins + customs).
 */

import { create } from 'zustand';
import {
  createCustomWorkType,
  deleteCustomWorkType,
  listExecutionRecipes,
  listRecipeVersions,
  updateCustomWorkType,
  type CustomWorkTypeInput,
} from '../../lib/executionRecipeService';
import type { ExecutionRecipeTemplate } from '../../types/work-type-recipes';

interface ExecutionRecipeState {
  recipes: ExecutionRecipeTemplate[];
  workspaceId: string | null;
  loading: boolean;
  error: string | null;
  loaded: boolean;

  fetchRecipes: (workspaceId?: string | null, force?: boolean) => Promise<void>;
  getRecipeByWorkType: (workType: string | null | undefined) => ExecutionRecipeTemplate | null;
  createCustom: (
    workspaceId: string,
    input: CustomWorkTypeInput,
  ) => Promise<{ data: ExecutionRecipeTemplate | null; error: string | null }>;
  updateCustom: (
    id: string,
    patch: Partial<Omit<CustomWorkTypeInput, 'work_type_ref'>>,
    reason?: string,
  ) => Promise<{ data: ExecutionRecipeTemplate | null; error: string | null }>;
  removeCustom: (id: string) => Promise<{ error: string | null }>;
  loadVersions: (
    workType: string,
  ) => Promise<{ data: ExecutionRecipeTemplate[]; error: string | null }>;
}

export const useExecutionRecipeStore = create<ExecutionRecipeState>((set, get) => ({
  recipes: [],
  workspaceId: null,
  loading: false,
  error: null,
  loaded: false,

  fetchRecipes: async (workspaceId, force = false) => {
    const wsId = workspaceId ?? null;
    const state = get();
    if (!force && state.loaded && state.workspaceId === wsId && !state.loading) return;

    set({ loading: true, error: null, workspaceId: wsId });
    const { data, error } = await listExecutionRecipes(wsId);
    set({ recipes: data, loading: false, error, loaded: !error, workspaceId: wsId });
  },

  getRecipeByWorkType: (workType) => {
    if (!workType) return null;
    return get().recipes.find((r) => r.work_type_ref === workType) ?? null;
  },

  createCustom: async (workspaceId, input) => {
    const result = await createCustomWorkType(workspaceId, input);
    if (!result.error) await get().fetchRecipes(workspaceId, true);
    return result;
  },

  updateCustom: async (id, patch, reason) => {
    const result = await updateCustomWorkType(id, patch, reason, get().workspaceId);
    if (!result.error) await get().fetchRecipes(get().workspaceId, true);
    return result;
  },

  removeCustom: async (id) => {
    const result = await deleteCustomWorkType(id);
    if (!result.error) await get().fetchRecipes(get().workspaceId, true);
    return result;
  },

  loadVersions: async (workType) => listRecipeVersions(workType, get().workspaceId),
}));
